import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandedListTab from '../ExpandedListTab';
import { manualCashLimitType } from '../../types';
import ReadonlyField from '../fields/ReadonlyField';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';
import DateTime, { LONG_DATE, LONG_DATETIME } from '../DateTime';
import Privileges from '../../utils/Privileges';
import { Privileged } from '../../hocs/withPrivileges';
import { setCopyOfManualCashLimitData } from '../../actions/copyOfManualCashLimit';
import ConfirmButton from '../buttons/ConfirmButton';
import DeleteButton from '../buttons/DeleteButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_MANUAL_CASH_LIMITS_QUERY_NAME, DELETE_MANUAL_CASH_LIMIT } from '../../graphql/manualCashLimits';

ManualCashLimitDetailTab.propTypes = {
    manualCashLimit: manualCashLimitType.isRequired,
    onDetailDelete: PropTypes.func.isRequired,
};

function ManualCashLimitDetailTab(props) {
    const {
        manualCashLimit,
        onDetailDelete,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const copyToCreate = () => dispatch(setCopyOfManualCashLimitData(manualCashLimit));
    const deleteManualCashLimitMutation = useExtendedMutation(DELETE_MANUAL_CASH_LIMIT, {
        variables: { id: manualCashLimit.id },
        successCallback: onDetailDelete,
        successMessageOptions: { key: 'manual-cash-limits.notification.manual-cash-limit-delete-success', dataProperty: 'deleteManualCashLimit' },
        failureMessageOptions: { key: 'manual-cash-limits.notification.manual-cash-limit-delete-failure', params: { memberName: manualCashLimit.member.name, memberId: manualCashLimit.member.memberId } },
        refetchQueries: [ALL_MANUAL_CASH_LIMITS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="manual-cash-limit-detail-tab"
            title={t('manual-cash-limits.manual-cash-limit-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <ReadonlyField
                    label={t('manual-cash-limits.member')}
                    value={`${manualCashLimit.member.name} (${manualCashLimit.member.memberId})`}
                    className="manual-cash-limit-detail-tab--fields--member"
                />
                <ReadonlyNumberField
                    label={t('manual-cash-limits.limit-value')}
                    value={manualCashLimit.limitValue}
                    fractionDigits={2}
                    className="manual-cash-limit-detail-tab--fields--limit-value"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.begin-date')}
                    value={<DateTime value={manualCashLimit.beginDate} format={LONG_DATE} />}
                    className="manual-cash-limit-detail-tab--fields--begin-date"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.end-date')}
                    value={<DateTime value={manualCashLimit.endDate} format={LONG_DATE} />}
                    className="manual-cash-limit-detail-tab--fields--end-date"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.currency')}
                    value={`${manualCashLimit.currency.name} (${manualCashLimit.currency.code})`}
                    className="manual-cash-limit-detail-tab--fields--currency"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.clearing-house')}
                    value={`${manualCashLimit.clearingHouse.name} (${manualCashLimit.clearingHouse.code})`}
                    className="manual-cash-limit-detail-tab--fields--clearing-house"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.creator')}
                    value={`${manualCashLimit.creator.name} (${manualCashLimit.creator.racf})`}
                    className="manual-cash-limit-detail-tab--fields--creator"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.creation-time')}
                    value={<DateTime value={manualCashLimit.creationTime} format={LONG_DATETIME} />}
                    className="manual-cash-limit-detail-tab--fields--creation-time"
                />
                <ReadonlyField
                    label={t('manual-cash-limits.description')}
                    value={manualCashLimit.description}
                    className="manual-cash-limit-detail-tab--fields--description"
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.MANUAL_CASH_LIMIT_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ConfirmButton
                            className="copy-to-create-button button-bordered button"
                            onConfirm={copyToCreate}
                            title={t('manual-cash-limits.copy-to-create')}
                            message={t('manual-cash-limits.confirm-copy-to-create')}
                        >
                            <FontAwesomeIcon icon="copy" />&nbsp;{t('manual-cash-limits.copy-to-create')}
                        </ConfirmButton>
                    </div>
                    <div className="rich-list-row-expanded--buttons--right">
                        <DeleteButton
                            extendedMutation={deleteManualCashLimitMutation}
                            confirmationMessage={t('manual-cash-limits.confirm-delete-manual-cash-limit', { memberName: manualCashLimit.member.name, memberId: manualCashLimit.member.memberId })}
                        />
                    </div>
                </div>
            </Privileged>
        </ExpandedListTab>
    );
}

export default ManualCashLimitDetailTab;
